import {
	AlertBar,
	Anchor,
	PersonAvatar,
	Space
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useStableImperativeCallback,
	useWorkspaceMediaQuery
 } from "client/Hooks";
import {
	isNil,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ExchangeEmailId,
	MailMessageLocationModel
} from "client/Api/Email/common";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import * as windowFactory from "client_legacy/ndaw/windowFactory";
import { EmailResponseKind, ViewEmailSessionState } from "../../EmailPage/EmailPage";
import {
	openExchangeEmailByExchangeId,
	EmailModel,
	MessageReadState,
	MessageFlagState
} from "client/Api/Email/email";
import { Attachments, AttachmentsHandle } from "./Attachements/Attachments";
import {
	Body,
	BodyHandle,
	forbidNonSecureContentHeader
} from "./Body/Body";
import { Meeting } from "./Meeting/Meeting";
import styles from "./EmailViewer.module.less";
import {
	EmailEvents,
	EmailIdWithIsReadStatus,
	EmailIdWithIsFlaggedStatus,
	subscribe
} from "client_legacy/Email/emailEvents";
import { getContactPhotoUrl } from "client/Features/AddressBook/common";
import classNames from "classnames";
import { ActionButtons } from "./ActionButtons/ActionButtons";
import { MobileAndDesktopHeaderDetails } from "./MobileAndDesktopHeaderDetails/MobileAndDesktopHeaderDetails";
import { EmailHeaderDetails } from "./EmailHeaderDetails/EmailHeaderDetails";
import { EmailActionModel } from "../../Actions/selectionActions";
import { unsubscribe } from "pubsub-js";
import { TimeZone } from "client/DateAndTime/timezones";

export type EmailViewerProps = {
	sessionState: ViewEmailSessionState;
	shouldAutomaticallyShowSenderImages: boolean;
	onAcceptMeetingInvite(
		emailId: ExchangeEmailId,
		isResponseRequested: boolean,
		isMessageRead: boolean
	): void;
	onDeclineMeetingInvite(
		emailId: ExchangeEmailId,
		isResponseRequested: boolean,
		isMessageRead: boolean
	): void;
	onOpenFolder: (smtpAddress: string | null, parentFolderId: string) => void;
	onRespondToEmail(
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	): void;
	onReadStatusChange(
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	): void;
	onFlagStatusChange(
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	): void;
	onDeleteEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onMoveEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onEmailBeingViewedChange(email: EmailActionModel | null): void;
	timezone: TimeZone;
	onIsBusyChange: (value: boolean) => void;
};

export const EmailViewer = (props: EmailViewerProps): ReactElement => {
	const {
		sessionState,
		shouldAutomaticallyShowSenderImages,
		onAcceptMeetingInvite,
		onDeclineMeetingInvite,
		onOpenFolder,
		onRespondToEmail,
		onFlagStatusChange,
		onReadStatusChange,
		onDeleteEmails,
		onMoveEmails,
		onEmailBeingViewedChange,
		timezone,
		onIsBusyChange: onIsBusyChangeFromProps
	} = props;

	const alertManager: AlertManager = useAlerts();
	const [emailModel, setEmailModel] = useState<EmailModel | undefined>(undefined);

	const onIsBusyChange: (value: boolean) => void = useStableImperativeCallback(onIsBusyChangeFromProps);

	const changeIsReadHandler = useCallback(
		(isRead: boolean): void => {
			setEmailModel(emailModel => {
				if (isNil(emailModel)) {
					return emailModel;
				}
				return {
					...emailModel,
					isRead
				};
			});
		},
		[]
	);

	const changeIsFlaggedHandler = useCallback(
		(isFlagged: boolean): void => {
			setEmailModel(emailModel => {
				if (isNil(emailModel)) {
					return emailModel;
				}
				return {
					...emailModel,
					isFlagged
				};
			});
		},
		[]
	);

	const changeFlagStatusHandler = (
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	): void => {
		onFlagStatusChange(
			emailIds,
			flagStatus,
			smtpAddress
		);
	};

	const changeReadStatusHandler = (
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	): void => {
		onReadStatusChange(
			emailIds,
			readStatus,
			smtpAddress
		);
	};

	usePubSubEvents(
		sessionState.emailId,
		changeIsFlaggedHandler,
		changeIsReadHandler
	);

	useEffect(
		() => {
			async function fetch(
				emailId: ExchangeEmailId,
				smtpAddress: string | null,
				signal: AbortSignal
			) {
				try {
					onIsBusyChange(true);
					const emailModel = await openExchangeEmailByExchangeId(
						{
							smtpAddress: isNotNil(smtpAddress)
								? {
									isSet: true,
									value: smtpAddress
								}
								: { isSet: false},
							messageId: emailId.id
						},
						signal
					);
					setEmailModel(emailModel);
					onIsBusyChange(false);

					const { parentFolderId } = emailModel;
					onOpenFolder(smtpAddress, parentFolderId.id);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					onIsBusyChange(false);
					return;
				}
			}

			const cancellation = new AbortController();
			fetch(
				sessionState.emailId,
				sessionState.smtpAddress,
				cancellation.signal
			);

			return () => cancellation.abort();
		},
		[
			alertManager,
			onOpenFolder,
			sessionState.emailId,
			sessionState.smtpAddress,
			onIsBusyChange
		]
	);

	return (
		<>
			{isNotNil(emailModel) && (
				<LoadedEmail
					emailModel={emailModel}
					smtpAddress={sessionState.smtpAddress}
					onIsBusyChange={onIsBusyChange}
					onAcceptMeetingInvite={onAcceptMeetingInvite}
					onDeclineMeetingInvite={onDeclineMeetingInvite}
					shouldAutomaticallyShowSenderImages={shouldAutomaticallyShowSenderImages}
					onRespondToEmail={onRespondToEmail}
					onChangeFlagStatus={changeFlagStatusHandler}
					onChangeReadStatus={changeReadStatusHandler}
					onDeleteEmails={onDeleteEmails}
					onMoveEmails={onMoveEmails}
					onEmailBeingViewedChange={onEmailBeingViewedChange}
					timezone={timezone}
				/>
			)}
		</>
	);
};
EmailViewer.displayName = "EmailViewer";

function usePubSubEvents(
	emailId: ExchangeEmailId,
	onChangeFlagStatus: (isFlagged: boolean) => void,
	onChangeReadStatus: (isRead: boolean) => void
): void {
	useEffect(
		(): () => void => {
			const subscriptionTokens: readonly PubSubSubscriptionToken[] = registerSubscriptions(
				emailId,
				onChangeFlagStatus,
				onChangeReadStatus
			);

			return () => {
				for (const token of subscriptionTokens) {
					unsubscribe(token);
				}
			};
		},
		[
			emailId,
			onChangeFlagStatus,
			onChangeReadStatus
		]
	);
}

function registerSubscriptions(
	currentEmailId: ExchangeEmailId,
	onChangeFlagStatus: (isFlagged: boolean) => void,
	onChangeReadStatus: (isRead: boolean) => void
): readonly PubSubSubscriptionToken[] {
	const tokens: PubSubSubscriptionToken[] = [];

	const changeIsReadToken: PubSubSubscriptionToken = subscribe(
		EmailEvents.emailReadStatusChanged,
		(_, data: EmailIdWithIsReadStatus) => {
			const {
				emailId,
				isRead
			} = data;

			if (emailId.id === currentEmailId.id) {
				onChangeReadStatus(isRead);
			}
		}
	);
	tokens.push(changeIsReadToken);

	const changeIsFlaggedToken: PubSubSubscriptionToken = subscribe(
		EmailEvents.emailFlagStatusChanged,
		(_, data: EmailIdWithIsFlaggedStatus) => {
			const {
				emailId,
				isFlagged
			} = data;

			if (emailId.id === currentEmailId.id) {
				onChangeFlagStatus(isFlagged);
			}
		}
	);
	tokens.push(changeIsFlaggedToken);

	return tokens;
}

type LoadedEmailProps = {
	emailModel: EmailModel;
	smtpAddress: string | null;
	shouldAutomaticallyShowSenderImages: boolean;
	onIsBusyChange(isBusy: boolean): void;
	onAcceptMeetingInvite(
		emailId: ExchangeEmailId,
		isResponseRequested: boolean,
		isMessageRead: boolean
	): void;
	onDeclineMeetingInvite(
		emailId: ExchangeEmailId,
		isResponseRequested: boolean,
		isMessageRead: boolean
	): void;
	onRespondToEmail(
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	): void;
	onChangeReadStatus(
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	): void;
	onChangeFlagStatus(
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	): void;
	onDeleteEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onMoveEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onEmailBeingViewedChange(email: EmailActionModel | null): void;
	timezone: TimeZone;
};

const LoadedEmail = (props: LoadedEmailProps): ReactElement => {
	const {
		emailModel,
		smtpAddress,
		shouldAutomaticallyShowSenderImages,
		onIsBusyChange,
		onAcceptMeetingInvite,
		onDeclineMeetingInvite,
		onRespondToEmail,
		onChangeFlagStatus,
		onChangeReadStatus,
		onDeleteEmails,
		onMoveEmails,
		onEmailBeingViewedChange,
		timezone
	} = props;

	const {
		exchangeEmailId,
		subject,
		ownerName,
		from,
		receivedDateTime,
		to,
		cc,
		importance,
		lastAction,
		lastActionDateTime,
		bodyType,
		body,
		attachments,
		isMeetingMessage,
		start,
		end,
		location,
		messageType,
		isRead,
		isResponseRequested,
		isFlagged
	} = emailModel;

	const [showFullHeaderDetails, setShowFullHeaderDetails] = useState<boolean>(false);
	const [shouldAllowUnsecureContent, setShouldAllowUnsecureContent] = useState<boolean>(false);
	const [hasUnsecureContent, setHasUnsecureContent] = useState<boolean>(false);

	const [shouldAllowInlineImages, setShouldAllowInlineImages] = useState<boolean>(shouldAutomaticallyShowSenderImages);
	const [hasInlineImages, setHasInlineImages] = useState<boolean>(false);
	const headerRef: RefObject<HTMLElement | null> = useRef<HTMLElement>(null);
	const bodyRef: RefObject<BodyHandle | null> = useRef<BodyHandle>(null);
	const attachmentsRef: RefObject<AttachmentsHandle | null> = useRef(null);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const messageLocation = useMemo(
		(): MailMessageLocationModel => {
			return {
				smtpAddress: isNotNil(smtpAddress)
					? {
						isSet: true,
						value: smtpAddress
					}
					: { isSet: false },
				messageId: exchangeEmailId
			};
		},
		[
			exchangeEmailId,
			smtpAddress
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(exchangeEmailId);

			setShouldAllowUnsecureContent(false);
			setHasUnsecureContent(false);
			setShouldAllowInlineImages(shouldAutomaticallyShowSenderImages);
			setHasInlineImages(false);
		},
		[
			exchangeEmailId,
			shouldAutomaticallyShowSenderImages
		]
	);

	useEffect(
		() => {
			onEmailBeingViewedChange({
				emailId: {
					id: exchangeEmailId
				},
				subject,
				isDraft: false,
				isFlagged,
				isRead,
				smtpAddress
			});

			return () => onEmailBeingViewedChange(null);
		},
		[
			exchangeEmailId,
			subject,
			isFlagged,
			isRead,
			smtpAddress,
			onEmailBeingViewedChange
		]
	);

	const acceptMeetingInviteHandler = useCallback(
		(): void => {
			onAcceptMeetingInvite(
				{id: exchangeEmailId},
				isResponseRequested,
				isRead
			);
		},
		[
			exchangeEmailId,
			isRead,
			isResponseRequested,
			onAcceptMeetingInvite
		]
	);

	const declineMeetingInviteHandler = useCallback(
		(): void => {
			onDeclineMeetingInvite(
				{id: exchangeEmailId},
				isResponseRequested,
				isRead
			);
		},
		[
			exchangeEmailId,
			isRead,
			isResponseRequested,
			onDeclineMeetingInvite
		]
	);

	const unsecureContentHandler = useCallback(
		(): void => {
			setHasUnsecureContent(true);
		},
		[]
	);

	const allowUnsecureContent = useCallback(
		(): void => {
			setShouldAllowUnsecureContent(true);
		},
		[]
	);

	const inlineImagesDetectedHandler = useCallback(
		(): void => {
			setHasInlineImages(true);
		},
		[]
	);

	const allowInlineImages = useCallback(
		(): void => {
			setShouldAllowInlineImages(true);
			if (!bodyRef.current) {
				console.error("Email body ref is not set");
				return;
			}

			onIsBusyChange(true);
			bodyRef.current
				.loadInlineImages()
				.finally(() => onIsBusyChange(false));

			// After 10 seconds of loading we hide the busy indicator to prevent endless loading
			// in the scenario exchange is slow
			setTimeout(
				() => onIsBusyChange(false),
				10000
			);
		},
		[onIsBusyChange]
	);

	const printHandler = useCallback(
		(): void => {
			if (!headerRef.current) {
				throw new Error("Email header is not rendered.");
			}

			if (!bodyRef.current) {
				throw new Error("Email body is not rendered.");
			}

			const printWindow: Window | null = windowFactory
				.open(
					"",
					"",
					"titlebar=0, toolbar=0, menubar=0, scrollbars=1, status=0"
				);
			if (!printWindow) {
				throw new Error("Failed to open a window for printing");
			}

			const documentHeadContents = document.head
				.innerHTML;

			const securityTag = shouldAllowUnsecureContent
				? ""
				: forbidNonSecureContentHeader;

			const headerHtml: string = headerRef.current
				.outerHTML;

			const emailHtml: string = bodyRef.current
				.getContent();

			printWindow.document
				.write(
					printTemplate(
						documentHeadContents + securityTag,
						headerHtml + emailHtml
					)
				);

			printWindow.document
				.close();

			const printCallback = (): void => {
				printWindow.focus();
				printWindow.print();
			};

			printWindow.addEventListener("load", printCallback);
		},
		[shouldAllowUnsecureContent]
	);

	const bodyPointerEventHandler = useCallback(
		(): void => {
			if (!attachmentsRef.current) {
				return;
			}

			attachmentsRef.current
				.closeDropOvers();
		},
		[]
	);

	const toggleDetails = useCallback(
		(): void => {
			if (isMobile) {
				setShowFullHeaderDetails(prevState => !prevState);
			}
		},
		[isMobile]
	);

	return (
		<article {...setTrackingId("email-viewer")}>
			{hasUnsecureContent && !shouldAllowUnsecureContent && (
				<>
					<div className={styles.alertContainer}>
						<AlertBar
							kind="info"
							isInline
							isDismissable={false}
							trackingId="insecure-content-alert"
						>
							<span>
								<span>{localize("_UnsecureContentIsHiddenMessage")}</span>
								<Space />
								<Anchor
									onClick={allowUnsecureContent}
									trackingId="allow"
								>
									{localize("_ClickHereToShowBlockedContent")}
								</Anchor>
							</span>
						</AlertBar>
					</div>
					<hr className={styles.divider} />
				</>
			)}
			<header
				{...setTrackingId("header")}
				ref={headerRef}
				className={styles.header}
			>
				<h2
					{...setTrackingId("subject")}
					className={styles.subject}
				>
					{isNotNilOrEmpty(subject)
						? subject
						: localize("_NoSubject")
					}
				</h2>
				<hr className={styles.divider} />
				<div className={classNames(
					styles.emailHeader,
					{[styles.emailHeader_hover]: isMobile}
				)}
					onClick={toggleDetails}
				>
					<div className={styles.avatarAndDetails}>
						<PersonAvatar
							initials={ownerName.length > 0 ? ownerName[0] : ""}
							imageSrc={getContactPhotoUrl(from)}
						/>
						<div className={styles.details}>
							<div className={styles.senderAndActionButtons}>
								<h4
									{...setTrackingId("sender")}
									className={styles.sender}
								>
									{ownerName}
									<Space />
									{!showFullHeaderDetails && (
										<>
											&lt;
											{isNotNilOrEmpty(from) ? from : <>&hellip;</>}
											&gt;
										</>
										)
									}
								</h4>
								<ActionButtons
									exchangeEmailId={exchangeEmailId}
									smtpAddress={smtpAddress}
									isFlagged={isFlagged}
									isMobile={isMobile}
									isRead={isRead}
									onChangeFlagStatus={onChangeFlagStatus}
									onChangeReadStatus={onChangeReadStatus}
									onDeleteEmails={onDeleteEmails}
									onMoveEmails={onMoveEmails}
									onRespondToEmail={onRespondToEmail}
									onPrint={printHandler}
									subject={subject}
								/>
							</div>
							<MobileAndDesktopHeaderDetails
								onAllowInlineImages={allowInlineImages}
								cc={cc}
								hasInlineImages={hasInlineImages}
								importance={importance}
								lastAction={lastAction}
								lastActionDateTime={lastActionDateTime}
								shouldAllowInlineImages={shouldAllowInlineImages}
								isMobile={isMobile}
								to={to}
								showFullHeaderDetails={showFullHeaderDetails}
								receivedDateTime={receivedDateTime}
								from={from}
								timezone={timezone}
							/>
						</div>
					</div>
					{
						isMobile && showFullHeaderDetails && (
							<EmailHeaderDetails
								onAllowInlineImages={allowInlineImages}
								cc={cc}
								hasInlineImages={hasInlineImages}
								importance={importance}
								lastAction={lastAction}
								lastActionDateTime={lastActionDateTime}
								shouldAllowInlineImages={shouldAllowInlineImages}
								isMobile={isMobile}
								to={to}
								showFullHeaderDetails={showFullHeaderDetails}
								receivedDateTime={receivedDateTime}
								timezone={timezone}
							/>
						)
					}
				</div>
				<hr className={styles.divider} />
			</header>
			{attachments.length > 0 && (
				<section className={styles.attachmentSection}>
					<Attachments
						ref={attachmentsRef}
						attachments={attachments}
						messageLocation={messageLocation}
					/>
					<hr className={styles.attachmentDivider} />
				</section>
			)}
			{isMeetingMessage && (
				<section className={styles.meetingSection}>
					<Meeting
						start={start}
						end={end}
						location={location}
						messageType={messageType}
						onAcceptMeetingInvite={acceptMeetingInviteHandler}
						onDeclineMeetingInvite={declineMeetingInviteHandler}
						timezone={timezone}
					/>
				</section>
			)}

			<section className={styles.bodySection}>
				<Body
					body={body}
					bodyKind={bodyType}
					shouldAllowUnsecureContent={shouldAllowUnsecureContent}
					shouldAutomaticallyShowSenderImages={shouldAutomaticallyShowSenderImages}
					onUnsecureContent={unsecureContentHandler}
					onInlineImagesDetected={inlineImagesDetectedHandler}
					ref={bodyRef}
					onPointerEvent={bodyPointerEventHandler}
				/>
			</section>
		</article>
	);
};
LoadedEmail.displayName = "LoadedEmail";

const printTemplate = (headContent: string, bodyContent: string): string => `<!DOCTYPE html>
<html>
<head>${headContent}</head>
<body>${bodyContent}</body>
</html>`;
